import { Trans, useLingui } from "@lingui/react/macro";
import { cloneDeep } from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { createPatch } from "../../../utils/patch";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { PhysiciansModal } from "../common/physicians-modal";
import { ClinicsItem } from "./clinics";

type ClinicEditDraft = {
  name: string;
  ambulance: string | null;
  address: string;
  country: string;
  region: number | null;
  phone: string | null;
  email: string | null;
  physicians: string[];
};

export const EditClinicDialog = observer(function EditClinicDialog({
  item,
  admin,
  onClose,
  onSave,
}: {
  item: ClinicsItem;
  admin: AdminConsole;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useLingui();
  const [original] = useState(() => createInitialDraft(item));
  const [draft] = useState(() =>
    observable<ClinicEditDraft>(cloneDeep(original)),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogHeader>
        <Trans>Edit clinic</Trans>
      </DialogHeader>
      <Form
        className="flex flex-col min-h-0 flex-1"
        onSubmit={async () => {
          const patch = createPatch({ ...draft }, original);
          if (Object.keys(patch).length === 0) {
            onClose();
            return;
          }
          await onSave(patch);
          onClose();
        }}
      >
        <DialogBody>
          <FormTable dataAdd>
            <FormRow
              label={t`Name`}
              data={<TextInput attr="name" model={draft} required />}
              mandatory
            />
            <FormRow
              label={t`Ambulance`}
              data={<TextInput attr="ambulance" model={draft} />}
            />
            <FormRow
              label={t`Address`}
              data={<TextInput attr="address" model={draft} required />}
              mandatory
            />
            <FormRow
              label={t`Country`}
              data={
                <CountrySelect
                  attr="country"
                  model={draft}
                  regionAttr="region"
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Region`}
              data={
                <RegionSelect
                  attr="region"
                  model={draft}
                  country={draft.country || undefined}
                  required
                />
              }
            />
            <FormRow
              label={t`(General) Phone`}
              data={<TextInput attr="phone" model={draft} />}
            />
            <FormRow
              label={t`(General) Email`}
              data={<TextInput attr="email" model={draft} />}
            />
            <FormRow
              label={t`Clinic physicians`}
              data={
                <ChooseField
                  count={draft.physicians.length}
                  modal={({ onClose: closeModal }) => {
                    const physicians = admin.createPhysicians();
                    return (
                      <PhysiciansModal
                        title={t`Change physicians for clinic: ${item.name}`}
                        preselected={draft.physicians}
                        filter={{ country: [draft.country] }}
                        mode="multi"
                        model={physicians}
                        onClose={closeModal}
                        onConfirm={({ selected }) => {
                          draft.physicians = selected;
                        }}
                      />
                    );
                  }}
                />
              }
            />
          </FormTable>
        </DialogBody>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit">
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
});

function createInitialDraft(item: ClinicsItem): ClinicEditDraft {
  return {
    name: item.name ?? "",
    ambulance: item.ambulance ?? null,
    address: item.address ?? "",
    country: item.country ?? "",
    region: item.region ?? null,
    phone: item.phone ?? null,
    email: item.email ?? null,
    physicians: item.physicians?.map((p) => p.uuid) ?? [],
  };
}
